import { ThemeProvider } from "@material-ui/core/styles";
import React, { useMemo } from "react";
import { Provider } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import envConfig from "./envConfig";
import { login } from "./model/oidc/actions";
import store from "./model/store";
import defaultTheme from "./themes";
import { Domain } from "./utils/constants";
import {
  useAppDispatch,
  useAuthenticate,
  useBroadcast,
} from "./utils/customHooks";

import "ol/ol.css";
import "./App.scss";
import "./Globals.module.scss";

function AppChildren({
  children,
  history,
  location,
  params,
}: {
  children: React.ReactNode;
  history: {
    // eslint-disable-next-line no-unused-vars
    push: (path: string) => void;
    // eslint-disable-next-line no-unused-vars
    replace: (path: string) => void;
  };
  location: {
    pathname: string;
  };
  params: Record<string, unknown>;
}) {
  const dispatch = useAppDispatch();
  const { isAuthComplete, isAuthenticated } = useAuthenticate();
  useBroadcast();

  if (!isAuthComplete) {
    // Only load the app once the authentication process is complete
    return <div></div>;
  }

  // http://localhost:8000/plans?draw.id=e1082d2d-f20e-4161-abfd-e219a572d122
  // On app domain there is a different workflow for authentication
  if (envConfig.app.domain === Domain.App && location.pathname === "/") {
    if (!isAuthenticated) {
      // we must be logged in mapset.app
      dispatch(login());
      return;
    }
  }

  return React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        history,
        location,
        params,
      });
    }
    return child;
  });
}

function App({ children }: { children: React.ReactNode }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const history = useMemo(() => {
    return {
      push: (path) => {
        navigate(path);
      },
      replace: (path) => {
        navigate(path);
      },
    };
  }, [navigate]);

  return (
    <Provider store={store}>
      <ThemeProvider theme={defaultTheme}>
        <AppChildren history={history} location={location} params={params}>
          {children}
        </AppChildren>
      </ThemeProvider>
    </Provider>
  );
}

export default React.memo(App);
